import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, getOrganizationSchema, getBreadcrumbSchema } from '../../utils/seo';

export default function Emergency() {
  // SEO Configuration
  useSEO({
    title: 'Emergency Care Request - The Villarreal Health Network',
    description: 'Urgent home healthcare assistance in Las Vegas. 24/7 emergency care coordination with 15-minute response time for immediate skilled nursing and medical support needs.',
    keywords: 'emergency home healthcare, urgent care request, 24/7 healthcare support, emergency nursing services, immediate medical care Las Vegas',
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "EmergencyService",
          "name": "Emergency Care Request",
          "description": "24/7 emergency home healthcare coordination",
          "provider": getOrganizationSchema(),
          "availableChannel": {
            "@type": "ServiceChannel",
            "servicePhone": {
              "@type": "ContactPoint",
              "telephone": "+1-702-555-7828",
              "contactType": "Emergency"
            }
          }
        },
        getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Emergency Care', url: '/emergency' }
        ])
      ]
    }
  });

  const [formData, setFormData] = useState({
    patientName: '',
    patientAge: '',
    emergencyContact: '',
    emergencyPhone: '',
    currentLocation: '',
    medicalCondition: '',
    urgentNeeds: [],
    currentSymptoms: '',
    medications: '',
    allergies: '',
    preferredHospital: '',
    insuranceInfo: '',
    additionalInfo: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const urgentNeeds = [
    'Immediate Skilled Nursing',
    'IV Therapy Setup',
    'Wound Care Emergency',
    'Medication Management Crisis',
    'Post-Hospital Discharge',
    'Respiratory Care',
    'Pain Management',
    'Mental Health Crisis',
    'Fall Risk Assessment',
    'Equipment Setup (Oxygen, etc.)'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox' && name === 'urgentNeeds') {
      setFormData(prev => ({
        ...prev,
        urgentNeeds: checked 
          ? [...prev.urgentNeeds, value]
          : prev.urgentNeeds.filter(item => item !== value)
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/submit-referral`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          ...formData,
          urgency: 'stat',
          referrerType: 'emergency',
          careTypes: formData.urgentNeeds
        })
      });

      const result = await response.json();

      if (result.success) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting emergency request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Emergency Hero Section */}
      <section className="bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white py-24 pt-36 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/159ae044fc8ab24fd05c3b7653b5395d.jpg')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-block bg-white/20 text-white px-8 py-4 rounded-full text-xl font-semibold backdrop-blur-sm shadow-lg animate-pulse">
                🚨 URGENT CARE REQUEST
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-8">
              Emergency Care Request
            </h1>
            <p className="text-2xl text-red-100 max-w-4xl mx-auto leading-relaxed">
              Need immediate home healthcare assistance? Submit your urgent care request and we'll respond within 15 minutes with emergency coordination.
            </p>
            
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <i className="ri-time-line text-white text-4xl mb-4"></i>
                <h3 className="text-white font-bold text-xl mb-2">15 Min Response</h3>
                <p className="text-red-100">Immediate coordination</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <i className="ri-phone-line text-white text-4xl mb-4"></i>
                <h3 className="text-white font-bold text-xl mb-2">24/7 Available</h3>
                <p className="text-red-100">Always here for you</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <i className="ri-shield-check-line text-white text-4xl mb-4"></i>
                <h3 className="text-white font-bold text-xl mb-2">Licensed Care</h3>
                <p className="text-red-100">Professional response</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact Banner */}
      <section className="bg-red-50 border-l-8 border-red-500 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <i className="ri-alarm-warning-line text-red-600 text-4xl mr-6"></i>
              <div>
                <h3 className="text-red-800 font-bold text-2xl">Life-Threatening Emergency?</h3>
                <p className="text-red-700 text-lg">Call 911 immediately for medical emergencies</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="tel:911">
                <Button variant="outline" className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Call 911
                </Button>
              </a>
              <a href="tel:+17025557828">
                <Button className="bg-red-600 hover:bg-red-700 whitespace-nowrap">
                  <i className="ri-customer-service-line mr-2"></i>
                  Emergency Line: (702) 555-STAT
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Form */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitStatus === 'success' && (
            <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-10 mb-12">
              <div className="flex items-center">
                <i className="ri-check-circle-line text-green-600 text-4xl mr-6"></i>
                <div>
                  <h3 className="text-green-800 font-bold text-2xl">Emergency Request Submitted!</h3>
                  <p className="text-green-700 text-lg">Our emergency coordinator will contact you within 15 minutes. Please keep your phone available.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-10 mb-12">
              <div className="flex items-center">
                <i className="ri-error-warning-line text-red-600 text-4xl mr-6"></i>
                <div>
                  <h3 className="text-red-800 font-bold text-2xl">Submission Error</h3>
                  <p className="text-red-700 text-lg">Please call our emergency line immediately: (702) 555-STAT</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-2xl p-12 border-2 border-red-100">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Emergency Care Request Form</h2>
              <p className="text-xl text-gray-600">Complete this form for urgent home healthcare needs</p>
            </div>

            {/* Patient Information */}
            <div className="mb-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                <i className="ri-user-line mr-4 text-red-600"></i>
                Patient Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Patient Name *
                  </label>
                  <input
                    type="text"
                    name="patientName"
                    value={formData.patientName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                    placeholder="Patient's full name"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Patient Age
                  </label>
                  <input
                    type="number"
                    name="patientAge"
                    value={formData.patientAge}
                    onChange={handleInputChange}
                    min="0"
                    max="120"
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                    placeholder="Age"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Emergency Contact Name *
                  </label>
                  <input
                    type="text"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                    placeholder="Contact person name"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Emergency Contact Phone *
                  </label>
                  <input
                    type="tel"
                    name="emergencyPhone"
                    value={formData.emergencyPhone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                    placeholder="(702) 555-0123"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Current Location *
                  </label>
                  <input
                    type="text"
                    name="currentLocation"
                    value={formData.currentLocation}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                    placeholder="Hospital, home address, or current location"
                  />
                </div>
              </div>
            </div>

            {/* Medical Information */}
            <div className="mb-16">
              <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                <i className="ri-heart-pulse-line mr-4 text-red-600"></i>
                Medical Information
              </h3>
              
              <div className="space-y-8">
                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Primary Medical Condition *
                  </label>
                  <input
                    type="text"
                    name="medicalCondition"
                    value={formData.medicalCondition}
                    onChange={handleInputChange}
                    required
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                    placeholder="Primary diagnosis or condition requiring urgent care"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Urgent Care Needs (select all that apply) *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {urgentNeeds.map((need) => (
                      <label key={need} className="flex items-center cursor-pointer p-4 border-2 border-gray-200 rounded-xl hover:bg-red-50 hover:border-red-300">
                        <input
                          type="checkbox"
                          name="urgentNeeds"
                          value={need}
                          checked={formData.urgentNeeds.includes(need)}
                          onChange={handleInputChange}
                          className="mr-4 h-5 w-5 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                        />
                        <span className="text-gray-700 font-medium">{need}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Current Symptoms or Concerns *
                  </label>
                  <textarea
                    name="currentSymptoms"
                    value={formData.currentSymptoms}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    maxLength={500}
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                    placeholder="Describe current symptoms, pain level, or urgent concerns..."
                  />
                  <p className="text-gray-500 mt-2">{formData.currentSymptoms.length}/500 characters</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Current Medications
                    </label>
                    <textarea
                      name="medications"
                      value={formData.medications}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                      placeholder="List current medications and dosages"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-gray-700 mb-4 text-lg">
                      Known Allergies
                    </label>
                    <textarea
                      name="allergies"
                      value={formData.allergies}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                      placeholder="List any known allergies"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center">
                <i className="ri-information-line mr-4 text-red-600"></i>
                Additional Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Preferred Hospital
                  </label>
                  <input
                    type="text"
                    name="preferredHospital"
                    value={formData.preferredHospital}
                    onChange={handleInputChange}
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                    placeholder="Preferred hospital if transfer needed"
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 mb-4 text-lg">
                    Insurance Information
                  </label>
                  <input
                    type="text"
                    name="insuranceInfo"
                    value={formData.insuranceInfo}
                    onChange={handleInputChange}
                    className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                    placeholder="Insurance provider and member ID"
                  />
                </div>
              </div>

              <div className="mt-8">
                <label className="block font-bold text-gray-700 mb-4 text-lg">
                  Additional Information
                </label>
                <textarea
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  rows={4}
                  maxLength={500}
                  className="w-full px-6 py-4 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 text-lg"
                  placeholder="Any other important information for our emergency response team..."
                />
                <p className="text-gray-500 mt-2">{formData.additionalInfo.length}/500 characters</p>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting || formData.urgentNeeds.length === 0}
                className="w-full md:w-auto bg-red-600 hover:bg-red-700 whitespace-nowrap text-xl px-12 py-5 shadow-xl"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line mr-3 animate-spin"></i>
                    Submitting Emergency Request...
                  </>
                ) : (
                  <>
                    <i className="ri-alarm-warning-line mr-3"></i>
                    Submit Emergency Request
                  </>
                )}
              </Button>
              
              <p className="text-gray-600 mt-8 text-lg">
                <i className="ri-time-line mr-2"></i>
                Emergency response within 15 minutes • Available 24/7
              </p>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}